import { actKey, entryArtistIds, entryInterestIds } from '@shared/lineupActs'
import type { LineupEntry, Set as FestivalSet } from '../types'
import type { BucketedUserIds } from '../hooks/useGroupInterests'

export interface CrewPick {
  kind: 'set' | 'artist'
  /** The set id, or the lineup act's key (`actKey`, the artist id for a solo act) for a lineup-only event. */
  id: string
  set?: FestivalSet
  /** The lineup act, for a lineup-only event. */
  entry?: LineupEntry
  /** The act's first artist, or a set's first artist (its artist-page fallback). */
  artistId?: string
  /** Distinct crew members who picked it, going first. */
  userIds: string[]
  goingUserIds: string[]
}

export type CrewPickFilter = 'all' | 'new' | 'mine'

/**
 * An event's sets ranked by the viewer's crew — or, before set times are out,
 * its lineup artists. Going outranks any amount of interest: it's where the
 * crew will actually be. Hearting one of a set's artists on this event's
 * lineup (`artistUserIds`) counts as interest in the set, the same way the
 * lineup facepile reads it. Only crew in `attendeeIds` (going to or interested
 * in this event, or holding a ticket) count — picks can outlive an RSVP.
 */
export function rankCrewTopPicks({
  sets,
  lineup,
  artistUserIds,
  setUserIds,
  attendeeIds,
}: {
  sets: FestivalSet[]
  lineup: LineupEntry[]
  artistUserIds: Record<string, string[]>
  setUserIds: BucketedUserIds
  attendeeIds: ReadonlySet<string>
}): CrewPick[] {
  const attending = (list: string[] | undefined) => (list ?? []).filter(id => attendeeIds.has(id))
  if (sets.length > 0) {
    const picks = sets.map((set): CrewPick => {
      const going = attending(setUserIds.going[set.id])
      const ids = new Set(going)
      for (const id of attending(setUserIds.interested[set.id])) ids.add(id)
      for (const a of entryInterestIds(set)) for (const id of attending(artistUserIds[a])) ids.add(id)
      return { kind: 'set', id: set.id, set, artistId: set.artistIds[0], userIds: [...ids], goingUserIds: going }
    })
    return picks
      .filter(p => p.userIds.length > 0)
      .sort((a, b) =>
        b.goingUserIds.length - a.goingUserIds.length ||
        b.userIds.length - a.userIds.length ||
        `${a.set!.day} ${a.set!.startTime}`.localeCompare(`${b.set!.day} ${b.set!.startTime}`),
      )
  }

  const seen = new Set<string>()
  const picks: CrewPick[] = []
  for (const entry of lineup) {
    const key = actKey(entry)
    if (seen.has(key)) continue
    seen.add(key)
    const userIds = [...new Set(entryInterestIds(entry).flatMap(a => attending(artistUserIds[a])))]
    if (userIds.length > 0) {
      picks.push({ kind: 'artist', id: key, entry, artistId: entryArtistIds(entry)[0], userIds, goingUserIds: [] })
    }
  }
  // Stable sort keeps lineup order among ties.
  return picks.sort((a, b) => b.userIds.length - a.userIds.length)
}

/** `isMine` says whether the viewer already marked a pick (or hearted its artist here). */
export function filterCrewPicks(
  picks: CrewPick[],
  filter: CrewPickFilter,
  isMine: (pick: CrewPick) => boolean,
): CrewPick[] {
  if (filter === 'all') return picks
  return picks.filter(p => isMine(p) === (filter === 'mine'))
}
